import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import moment from "moment";

import StatisticQueryInput from "../components/StatisticQueryInput";
import StatisticChart from "../components/StatisticChart";
import StatisticDetailTableV2 from "../components/StatisticDetailTableV2";
import Loading from "../components/Loading";
import Http from "../services/http";

const StatisticComprehensiveV2 = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [queryResult, setQueryResult] = useState(null);
  const [queryParams, setQueryParams] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get default dates
  const getDefaultDates = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const sixMonthsAgo = moment().subtract(6, "months");
    const startDefaultYear = sixMonthsAgo.year();
    const startDefaultMonth = (sixMonthsAgo.month() + 1)
      .toString()
      .padStart(2, "0");

    return {
      startYear: startDefaultYear.toString(),
      startMonth: startDefaultMonth,
      endYear: currentYear.toString(),
      endMonth: currentMonth.toString().padStart(2, "0"),
    };
  };

  // Get initial params from URL or defaults
  const getInitialParams = () => {
    const defaults = getDefaultDates();
    const isUnlimitedTime = searchParams.get("isUnlimitedTime");

    return {
      startYear: searchParams.get("startYear") || defaults.startYear,
      startMonth: searchParams.get("startMonth") || defaults.startMonth,
      endYear: searchParams.get("endYear") || defaults.endYear,
      endMonth: searchParams.get("endMonth") || defaults.endMonth,
      isUnlimitedTime: isUnlimitedTime || "false",
      constructionWorkID: searchParams.get("constructionWorkID") || "",
      constructionWorkName: searchParams.get("constructionWorkName") || "",
      supplierID: searchParams.get("supplierID") || "",
      supplierName: searchParams.get("supplierName") || "",
    };
  };

  // Fetch data using the current parameters
  const fetchData = async (params) => {
    setIsLoading(true);
    try {
      const queryParams = {
        startYear:
          params.isUnlimitedTime === "true" ? undefined : params.startYear,
        startMonth:
          params.isUnlimitedTime === "true" ? undefined : params.startMonth,
        endYear: params.isUnlimitedTime === "true" ? undefined : params.endYear,
        endMonth:
          params.isUnlimitedTime === "true" ? undefined : params.endMonth,
        constructionWorkID: params.constructionWorkID || undefined,
        supplierID: params.supplierID || undefined,
      };

      const response = await Http.get("auth/statistic/comprehensiveV2", {
        params: queryParams,
      });
      setQueryResult(response);
      setQueryParams(params);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Update URL and fetch data when query parameters change
  const handleQueryResult = async (result, params) => {
    // Update URL parameters
    const newSearchParams = new URLSearchParams();

    // Handle time values
    if (params.startYear) {
      newSearchParams.set("startYear", params.startYear);
      newSearchParams.set("startMonth", params.startMonth);
    }
    if (params.endYear) {
      newSearchParams.set("endYear", params.endYear);
      newSearchParams.set("endMonth", params.endMonth);
    }

    // Handle unlimited time state
    if (params.isUnlimitedTime === "true") {
      newSearchParams.set("isUnlimitedTime", "true");
    }

    // Handle other parameters
    if (params.constructionWorkID) {
      newSearchParams.set("constructionWorkID", params.constructionWorkID);
    }
    if (params.constructionWorkName) {
      newSearchParams.set("constructionWorkName", params.constructionWorkName);
    }
    if (params.supplierID) {
      newSearchParams.set("supplierID", params.supplierID);
    }
    if (params.supplierName) {
      newSearchParams.set("supplierName", params.supplierName);
    }

    setSearchParams(newSearchParams);

    // Update state and fetch data
    setQueryResult(result);
    setQueryParams(params);
  };

  // Initial fetch on component mount
  useEffect(() => {
    const initialParams = getInitialParams();

    // 如果 URL 中沒有參數，則使用默認值並更新 URL
    if (!searchParams.toString()) {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set("startYear", initialParams.startYear);
      newSearchParams.set("startMonth", initialParams.startMonth);
      newSearchParams.set("endYear", initialParams.endYear);
      newSearchParams.set("endMonth", initialParams.endMonth);
      newSearchParams.set("isUnlimitedTime", initialParams.isUnlimitedTime);
      if (initialParams.constructionWorkID) {
        newSearchParams.set(
          "constructionWorkID",
          initialParams.constructionWorkID
        );
      }
      if (initialParams.constructionWorkName) {
        newSearchParams.set(
          "constructionWorkName",
          initialParams.constructionWorkName
        );
      }
      if (initialParams.supplierID) {
        newSearchParams.set("supplierID", initialParams.supplierID);
      }
      if (initialParams.supplierName) {
        newSearchParams.set("supplierName", initialParams.supplierName);
      }
      setSearchParams(newSearchParams);
    }

    // 如果有任何參數，則進行查詢
    if (Object.values(initialParams).some((value) => value)) {
      fetchData(initialParams);
    }
  }, []);

  const formatMoney = (amount) => {
    return amount?.toLocaleString();
  };

  const getDisplayText = () => {
    if (!queryParams) return null;

    const constructionText = queryParams.constructionWorkName || "所有工程";
    const supplierText = queryParams.supplierName || "所有廠商";

    // 處理日期範圍顯示
    let dateRange;
    if (queryParams.isUnlimitedTime === "true") {
      dateRange = "不限時間";
    } else {
      dateRange = `${queryParams.startYear} (${
        parseInt(queryParams.startYear) - 1911
      }) 年 ${queryParams.startMonth} 月份 - ${queryParams.endYear} (${
        parseInt(queryParams.endYear) - 1911
      }) 年 ${queryParams.endMonth} 月份`;
    }

    return {
      dateRange,
      construction: `工程：${constructionText}`,
      supplier: `廠商：${supplierText}`,
      total: `總支出：$ ${formatMoney(queryResult?.totalCostAmount || 0)} 元`,
    };
  };

  const displayText = getDisplayText();

  return (
    <div className="w-full">
      {isLoading && <Loading />}
      <div className="mx-10 mt-10 text-4xl font-bold">收支統計</div>
      <StatisticQueryInput
        onQueryResult={handleQueryResult}
        setIsLoading={setIsLoading}
        initialParams={getInitialParams()}
      />

      {queryResult && (
        <div className="mx-10 mt-5 flex flex-col items-center justify-between gap-2">
          <div className="text-2xl font-bold">{displayText.dateRange}</div>
          <div className="text-2xl font-bold">{displayText.construction}</div>
          <div className="text-2xl font-bold">{displayText.supplier}</div>
          <div className="text-2xl font-bold">{displayText.total}</div>
        </div>
      )}

      <StatisticChart queryResult={queryResult} queryParams={queryParams} />

      <div className="mx-10 mt-10 text-4xl font-bold">支出明細</div>
      <StatisticDetailTableV2 detailList={queryResult?.detailList || []} />
    </div>
  );
};

export default StatisticComprehensiveV2;
